import { isEmpty, isNil } from 'lodash';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { View } from '@shared/components/View';
import { MBAutosizeTextInput } from '@shared/components/textInput';
import { MBChatInputUtil } from '@shared/scenes/chat/input/util/index';
import { API } from '@shared/types/api';
import { MBAnalytics } from '@shared/util/analytics';
import { useChannelMemberCount } from '@shared/util/hooks/useChannelMemberCount';
import { MBChatInputBar } from '../containers/bar';
import { ChatInputGIFModal } from '../containers/gifModal';
import { MBUrgentBanner } from '../containers/urgentBanner';
import { useChannelMentionUsers } from '../hooks/useChannelMentionUsers';
import { ChatInputBarProps } from './bar';
import { MENTION_ITEM_LIMIT } from './constants';
import { MentionOption, MentionsMenu } from './mention/MentionsMenu';

export interface ChatInputProps {
  draftId: string;
  initialValue?: string;
  autoFocus?: boolean;
  isEditing?: boolean;
  placeholder?: ChatInputBarProps['placeholder'];
  hideUrgent?: ChatInputBarProps['hideUrgent'];
  channelId?: number;
  groups: API.ChannelGroupFragment[];
  /**
   * Handles the message. Resolving *false* means the text was not taken and stays in the input
   * — see `handleSend`. Anything that always sends can return nothing at all.
   */
  onSend: (message: string) => void | boolean | Promise<void | boolean>;
  onCancelEdit: () => void;
  onBlur?: () => void;
  onFocus?: () => void;
  onUnmount?(message: string): void;
  onChangeText?: (message: string) => void;
  paddingHorizontal?: number;
}

export function ChatInputComponent({
  draftId,
  initialValue,
  autoFocus,
  isEditing,
  placeholder,
  hideUrgent,
  channelId,
  groups,
  onSend,
  onCancelEdit,
  onBlur,
  onFocus,
  onUnmount,
  onChangeText,
  paddingHorizontal = 2,
}: ChatInputProps) {
  const [value, setValue] = useState(initialValue || '');
  const [cursorPosition, setCursorPosition] = useState(initialValue ? initialValue.length : 0);
  const [isInputFocused, setIsInputFocused] = useState(false);

  const inputBar = useRef<MBAutosizeTextInput>(null);

  // Keep a ref to the latest value so the unmount cleanup captures it correctly
  const valueRef = useRef(value);
  valueRef.current = value;

  useEffect(() => {
    return () => {
      onUnmount?.(valueRef.current);
    };
  }, [onUnmount]);

  // The current @mention query (text after `@`), or null when the cursor is not
  // inside a mention. The hook narrows the channel roster (in-memory for small
  // channels, debounced server-side filter for large ones).
  const query = useMemo(
    () => MBChatInputUtil.mentionQuery(value, cursorPosition) ?? null,
    [value, cursorPosition],
  );
  const { users: mentionUsers, hasMore: hasMoreUsers } = useChannelMentionUsers({
    channelId,
    query,
  });

  const { mentionOptions, hasMoreMentions } = useMemo(() => {
    if (isNil(query)) {
      return { mentionOptions: [], hasMoreMentions: false };
    }
    const filteredGroups = isEmpty(query) ? groups : groups.filter(g => g.id.includes(query));
    const all: MentionOption[] = [
      ...filteredGroups.map(g => ({ kind: 'group' as const, data: g })),
      ...mentionUsers.map(u => ({ kind: 'user' as const, data: u })),
    ];
    return {
      mentionOptions: all.slice(0, MENTION_ITEM_LIMIT),
      hasMoreMentions: hasMoreUsers || all.length > MENTION_ITEM_LIMIT,
    };
  }, [query, groups, mentionUsers, hasMoreUsers]);

  const { count: memberCount } = useChannelMemberCount({ channelId });
  const numUrgentRecipients = MBChatInputUtil.numUrgentRecipients(value, memberCount);

  const handleChangeText = useCallback(
    (newValue: string) => {
      setValue(newValue);
      onChangeText?.(newValue);
    },
    [onChangeText],
  );

  /*
   * When we click on a mention, we replace the current mention word
   * with the full mention.
   * TODO(fant): add unit tests for this logic.
   */
  const onAddMention = useCallback(
    (mention: string): number => {
      const { string, cursorPosition: newCursorPosition } = MBChatInputUtil.addMention(
        value,
        cursorPosition,
        mention,
      );
      setValue(string);
      setCursorPosition(newCursorPosition);
      onChangeText?.(string);
      return newCursorPosition;
    },
    [value, cursorPosition, onChangeText],
  );

  const onPressUser = useCallback(
    (user: API.ShortUserFragment) => {
      MBAnalytics.track('Chat::Input::Mention::PressUser', { id: user.id, draftId });
      const newCursorPosition = onAddMention(user.username);
      inputBar.current?.focus();
      requestAnimationFrame(() => inputBar.current?.setCursorPosition(newCursorPosition));
    },
    [draftId, onAddMention],
  );

  const onPressGroup = useCallback(
    (group: API.ChannelGroupFragment) => {
      MBAnalytics.track('Chat::Input::Mention::PressGroup', { id: group.id, draftId });
      const newCursorPosition = onAddMention(group.id);
      inputBar.current?.focus();
      requestAnimationFrame(() => inputBar.current?.setCursorPosition(newCursorPosition));
    },
    [draftId, onAddMention],
  );

  const onAddEmoji = useCallback(
    (emoji: string) => {
      const before = value.substr(0, cursorPosition);
      const after = value.substr(cursorPosition);
      const newValue = [before, emoji, after].join('');
      handleChangeText(newValue);
      setCursorPosition(cursorPosition + emoji.length);
    },
    [value, cursorPosition, handleChangeText],
  );

  const handleSend = useCallback(() => {
    const sent = value;
    // The input is cleared only once whoever handles the send says it has the text. A `/ask`
    // that puts a question to the user first ("continue this conversation, or start a new
    // one?") can be closed without answering, and the text has to still be here if it is.
    void Promise.resolve(onSend(sent)).then(handled => {
      // Anything typed while the send was in flight is newer than what was sent, and clearing
      // it would take away text the user never sent.
      // Only an explicit refusal keeps the text: a handler with nothing to say about it always
      // sent it, which is every caller but the `/ask` command.
      if (handled === false || valueRef.current !== sent) {
        return;
      }
      setValue('');
      setCursorPosition(0);
      onChangeText?.('');
    });
  }, [onSend, value, onChangeText]);

  const handleCancelEdit = useCallback(() => {
    setValue('');
    onCancelEdit();
  }, [onCancelEdit]);

  return (
    <View relative paddingHorizontal={paddingHorizontal}>
      <MentionsMenu
        anchorName="--chat-input"
        options={mentionOptions}
        hasMore={hasMoreMentions}
        lightDismiss={!isInputFocused}
        onClickUser={onPressUser}
        onClickGroup={onPressGroup}
      />

      <MBChatInputBar
        innerRef={inputBar}
        draftId={draftId}
        autoFocus={autoFocus}
        isEditing={isEditing ?? false}
        hideUrgent={hideUrgent}
        value={value}
        placeholder={placeholder}
        onSend={handleSend}
        onCancelEdit={handleCancelEdit}
        onBlur={() => {
          setIsInputFocused(false);
          onBlur?.();
        }}
        onFocus={() => {
          setIsInputFocused(true);
          onFocus?.();
        }}
        onChangeText={handleChangeText}
        onSubmitEditing={handleSend}
        onCursorPositionChange={setCursorPosition}
        onAddEmoji={onAddEmoji}
      />

      {numUrgentRecipients > 0 && <MBUrgentBanner draftId={draftId} count={numUrgentRecipients} />}
      <ChatInputGIFModal draftId={draftId} />
    </View>
  );
}
